import { Link } from '@inertiajs/react'
import Seo from '../../components/Seo'
import type { SeoProps } from '../../types'
import ClaimStateBadge from '../../components/ClaimStateBadge'
import SourceEvidence from '../../components/SourceEvidence'
import { eyebrowClass, linkClass } from '../../components/ui'
import { retrievalDate, sourceUrl, SPEC_COLUMNS, specText } from '../../lib/catalog'
import type { Description, ShaftDetail, SourceRef, SpecAttribute, SpecValue, VariantDetail } from '../../types/catalog'

type Props = { seo: SeoProps; shaft: ShaftDetail; variants: VariantDetail[]; sources: SourceRef[] }

function SpecCell({ spec, sources, attribute }: { spec: SpecValue | null; sources: SourceRef[]; attribute: SpecAttribute }) {
  if (!spec) return <span aria-label="Not published or not verified" className="font-mono text-muted">—</span>
  return <div className="space-y-2">
    <ClaimStateBadge state={spec.state} />
    {spec.state === 'conflicting' ? <ul className="space-y-4">{spec.alternatives.map((alternative, index) => <li key={index}>
      <p className="font-mono text-base tabular-nums">{specText(alternative.value, spec.unit, attribute)}</p>
      <SourceEvidence source={sources.find(source => source.id === alternative.source_id)} quote={alternative.quote} />
    </li>)}</ul> : <>
      <p className="font-mono text-base tabular-nums">{specText(spec.value, spec.unit, attribute)}</p>
      <SourceEvidence source={sources.find(source => source.id === spec.source_id)} quote={spec.quote} />
    </>}
  </div>
}

function ManufacturerDescription({ label, description, sources }: { label: string; description: Description; sources: SourceRef[] }) {
  return <section className="min-w-0 rounded-xl border border-line bg-surface p-6">
    <h2 className={`${eyebrowClass} text-muted`}>{label} · Manufacturer's description</h2>
    <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-7">{description.description ?? 'No verified description available.'}</p>
    {description.description && <div className="mt-4"><ClaimStateBadge state={description.state} /><SourceEvidence source={sources.find(source => source.id === description.source_id)} quote={description.quote} /></div>}
  </section>
}

export default function ShaftsShow({ shaft, variants, sources, seo }: Props) {
  return <>
    <Seo seo={seo} />
    <Link href="/shafts" className={`${linkClass} text-sm`}>← Shaft catalog</Link>
    <header className="mt-7 mb-8">
      <p className={`${eyebrowClass} text-brand`}>{shaft.brand}</p>
      <h1 className="mt-3 break-words font-serif text-4xl tracking-tight sm:text-5xl">{shaft.name}</h1>
      <p className="mt-4 text-sm text-muted">{[shaft.generation, ...shaft.club_types].filter(Boolean).join(' · ')}</p>
      <p className="mt-4 max-w-2xl text-base leading-7 text-muted">Published specifications, with the evidence beside them. A shaft’s description is a starting point for testing, not a promise about your ball flight.</p>
    </header>
    <div className="mb-10 grid gap-4 sm:grid-cols-2">
      <ManufacturerDescription label="Launch" description={shaft.launch} sources={sources} />
      <ManufacturerDescription label="Spin" description={shaft.spin} sources={sources} />
    </div>
    <section aria-labelledby="variants-title">
      <h2 id="variants-title" className="font-serif text-3xl">The specifications, variant by variant</h2>
      <p className="mt-3 mb-5 max-w-3xl text-sm leading-7 text-muted">Flex labels aren’t standardized across brands. Weight class is the manufacturer’s label; raw weight is the published measurement. — means not published / not verified.</p>
      {variants.length ? <div role="region" aria-label="Shaft variant specifications" tabIndex={0} className="overflow-x-auto rounded-xl border border-line bg-surface focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{shaft.name} variant specifications and evidence</caption>
          <thead className="border-b border-line bg-soft"><tr><th scope="col" className="sticky left-0 z-10 min-w-36 bg-soft px-5 py-4">Variant</th>{SPEC_COLUMNS.map(column => <th key={column.key} scope="col" className="min-w-52 px-5 py-4 font-medium">{column.label}</th>)}</tr></thead>
          <tbody className="divide-y divide-line">{variants.map(variant => <tr key={variant.id}>
            <th scope="row" className="sticky left-0 min-w-36 bg-surface px-5 py-5 align-top font-normal"><span className="block font-semibold">{variant.weight_class} / {variant.flex}</span><span className="mt-2 block text-xs text-muted">Weight class / flex</span></th>
            {SPEC_COLUMNS.map(column => <td key={column.key} className="min-w-52 max-w-xs px-5 py-5 align-top"><SpecCell spec={variant.specs[column.key]} sources={sources} attribute={column.key} /></td>)}
          </tr>)}</tbody>
        </table>
      </div> : <p className="rounded-lg border border-line bg-surface p-6 text-sm text-muted">No verified variants are available yet. The catalog is growing.</p>}
      <p className="mt-3 text-xs leading-6 text-muted">Scroll across for all measurements. Open an excerpt to see the source wording behind a claim.</p>
    </section>
    <section aria-labelledby="sources-title" className="mt-10 border-t border-line pt-8">
      <p className={`${eyebrowClass} text-brand`}>Follow the evidence</p>
      <h2 id="sources-title" className="mt-3 font-serif text-3xl">Sources</h2>
      {sources.length ? <ul className="mt-5 divide-y divide-line">{sources.map(source => {
        const url = sourceUrl(source.url)
        return <li id={`source-${source.id}`} key={source.id} className="py-5">
          {url ? <a href={url} target="_blank" rel="noopener noreferrer" className={linkClass}>{source.title}</a> : <p className="font-medium">{source.title}</p>}
          <p className="mt-2 text-xs leading-6 text-muted">{source.kind.toUpperCase()} · {retrievalDate(source.retrieved_at)}</p>
          {url && <p className="mt-1 break-all text-xs text-muted">{url}</p>}
        </li>
      })}</ul> : <p className="mt-4 text-sm text-muted">No source references are available yet.</p>}
    </section>
  </>
}
